import { DOWNLOAD_FORMAT, DOWNLOAD_FORMAT_TYPES } from "constants/bookkeeping";
import {
  SITOverviewResponse,
  SITSalesData,
  SITOverviewSortBy,
  SITSalesSortBy,
  SITPayrollSortBy,
  SITPropertySortBy,
  SITSortOrder,
  SITSource,
  SITSettingsResponse,
  SITStateSettings,
  SITApportionmentFactor,
  SITTaxRateCondition,
  SITPatchTaxableIncomeResponse,
  SITSalesDataTemplateResponse,
  SITPayrollData,
  SITPayrollTemplateResponse,
  SITPropertyResponse,
  SITPropertyItem,
  SITNexusThresholdPayload,
  SITOverviewExportResponse,
} from "types/Models/stateIncomeTax";
import { emptyApi } from "./emptyApi";

const BASE = (groupId: string, entityId: string) =>
  `/api/inkle/state_income_tax/group/${groupId}/entity/${entityId}/state_income_tax`;

export const stateIncomeTaxApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getSITOverview: build.query<
      SITOverviewResponse,
      {
        groupId: string;
        entityId: string;
        season: number;
        search?: string;
        source?: SITSource[];
        nexus?: string;
        sort_by?: SITOverviewSortBy;
        sort_order?: SITSortOrder;
      }
    >({
      query: ({
        groupId,
        entityId,
        season,
        search,
        source,
        nexus,
        sort_by,
        sort_order,
      }) => ({
        url: `${BASE(groupId, entityId)}/overview/`,
        params: {
          season,
          search,
          source: source?.length ? source.join(",") : undefined,
          nexus,
          sort_by,
          sort_order,
        },
      }),
      providesTags: ["STATE_INCOME_TAX"],
    }),

    getSITSalesData: build.query<
      SITSalesData | null,
      {
        groupId: string;
        entityId: string;
        season: number;
        sort_by?: SITSalesSortBy;
        sort_order?: SITSortOrder;
      }
    >({
      query: ({ groupId, entityId, season, sort_by, sort_order }) => ({
        url: `${BASE(groupId, entityId)}/sales_data/`,
        params: { season, sort_by, sort_order },
      }),
      providesTags: ["STATE_INCOME_TAX_SALES_DATA"],
    }),

    uploadSITSalesData: build.mutation<
      SITSalesData,
      { groupId: string; entityId: string; season: number; file: File }
    >({
      query: ({ groupId, entityId, season, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `${BASE(groupId, entityId)}/sales_data/`,
          method: "POST",
          params: { season },
          body: formData,
        };
      },
      invalidatesTags: ["STATE_INCOME_TAX_SALES_DATA", "STATE_INCOME_TAX"],
    }),

    deleteSITSalesData: build.mutation<
      void,
      { groupId: string; entityId: string; season: number }
    >({
      query: ({ groupId, entityId, season }) => ({
        url: `${BASE(groupId, entityId)}/sales_data/`,
        method: "DELETE",
        params: { season },
      }),
      invalidatesTags: ["STATE_INCOME_TAX_SALES_DATA", "STATE_INCOME_TAX"],
    }),

    getSITSalesDataTemplate: build.query<
      SITSalesDataTemplateResponse,
      { groupId: string; entityId: string }
    >({
      query: ({ groupId, entityId }) => ({
        url: `${BASE(groupId, entityId)}/sales_data/template/`,
      }),
    }),

    // Payroll
    getSITPayrollData: build.query<
      SITPayrollData | null,
      {
        groupId: string;
        entityId: string;
        season: number;
        sort_by?: SITPayrollSortBy;
        sort_order?: SITSortOrder;
      }
    >({
      query: ({ groupId, entityId, season, sort_by, sort_order }) => ({
        url: `${BASE(groupId, entityId)}/payroll_data/`,
        params: { season, sort_by, sort_order },
      }),
      providesTags: ["STATE_INCOME_TAX_PAYROLL_DATA"],
    }),

    uploadSITPayrollData: build.mutation<
      SITPayrollData,
      { groupId: string; entityId: string; season: number; file: File }
    >({
      query: ({ groupId, entityId, season, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `${BASE(groupId, entityId)}/payroll_data/`,
          method: "POST",
          params: { season },
          body: formData,
        };
      },
      invalidatesTags: ["STATE_INCOME_TAX_PAYROLL_DATA", "STATE_INCOME_TAX"],
    }),

    deleteSITPayrollData: build.mutation<
      void,
      { groupId: string; entityId: string; season: number }
    >({
      query: ({ groupId, entityId, season }) => ({
        url: `${BASE(groupId, entityId)}/payroll_data/`,
        method: "DELETE",
        params: { season },
      }),
      invalidatesTags: ["STATE_INCOME_TAX_PAYROLL_DATA", "STATE_INCOME_TAX"],
    }),

    getSITPayrollTemplate: build.query<
      SITPayrollTemplateResponse,
      { groupId: string; entityId: string }
    >({
      query: ({ groupId, entityId }) => ({
        url: `${BASE(groupId, entityId)}/payroll_data/template/`,
      }),
    }),

    // Property
    getSITPropertyData: build.query<
      SITPropertyResponse | null,
      {
        groupId: string;
        entityId: string;
        season: number;
        sort_by?: SITPropertySortBy;
        sort_order?: SITSortOrder;
      }
    >({
      query: ({ groupId, entityId, season, sort_by, sort_order }) => ({
        url: `${BASE(groupId, entityId)}/property_data/`,
        params: { season, sort_by, sort_order },
      }),
      providesTags: ["STATE_INCOME_TAX_PROPERTY_DATA"],
    }),

    addSITPropertyData: build.mutation<
      SITPropertyItem,
      {
        groupId: string;
        entityId: string;
        season: number;
        state_uuid: string;
        owned_property: string;
        rented_property: string;
      }
    >({
      query: ({ groupId, entityId, season, ...body }) => ({
        url: `${BASE(groupId, entityId)}/property_data/`,
        method: "POST",
        params: { season },
        body,
      }),
      invalidatesTags: ["STATE_INCOME_TAX_PROPERTY_DATA", "STATE_INCOME_TAX"],
    }),

    deleteSITPropertyData: build.mutation<
      void,
      { groupId: string; entityId: string; season: number; state_uuid: string }
    >({
      query: ({ groupId, entityId, season, state_uuid }) => ({
        url: `${BASE(groupId, entityId)}/property_data/`,
        method: "DELETE",
        params: { season },
        body: { state_uuid },
      }),
      invalidatesTags: ["STATE_INCOME_TAX_PROPERTY_DATA", "STATE_INCOME_TAX"],
    }),

    // Settings
    getSITSettings: build.query<
      SITSettingsResponse,
      { groupId: string; entityId: string; season: number }
    >({
      query: ({ groupId, entityId, season }) => ({
        url: `${BASE(groupId, entityId)}/settings/`,
        params: { season },
      }),
      providesTags: ["STATE_INCOME_TAX_SETTINGS"],
    }),

    saveSITSettings: build.mutation<
      SITStateSettings,
      {
        groupId: string;
        entityId: string;
        season: number;
        state_uuid: string;
        apportionment_factor: SITApportionmentFactor;
        tax_rate_conditions: Omit<SITTaxRateCondition, "uuid">[];
        nexus_thresholds: SITNexusThresholdPayload[];
      }
    >({
      query: ({ groupId, entityId, season, ...body }) => ({
        url: `${BASE(groupId, entityId)}/settings/`,
        method: "PUT",
        params: { season },
        body,
      }),
      invalidatesTags: ["STATE_INCOME_TAX_SETTINGS", "STATE_INCOME_TAX"],
    }),

    patchSITTaxableIncome: build.mutation<
      SITPatchTaxableIncomeResponse,
      {
        groupId: string;
        entityId: string;
        season: number;
        state_uuid: string;
        taxable_income: string | null;
      }
    >({
      query: ({ groupId, entityId, season, ...body }) => ({
        url: `${BASE(groupId, entityId)}/taxable_income/`,
        method: "PATCH",
        params: { season },
        body,
      }),
      invalidatesTags: ["STATE_INCOME_TAX"],
    }),

    exportSITOverview: build.mutation<
      SITOverviewExportResponse,
      {
        groupId: string;
        entityId: string;
        season: number;
        action: DOWNLOAD_FORMAT_TYPES;
        export_to_mail: boolean;
        folder_id?: string;
      }
    >({
      query: ({ groupId, entityId, season, export_to_mail, folder_id }) => ({
        url: `${BASE(groupId, entityId)}/overview/`,
        method: "GET",
        params: {
          season,
          action: DOWNLOAD_FORMAT.DOWNLOAD_EXCEL,
          export_to_mail,
          folder_id,
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSITOverviewQuery,
  useGetSITSalesDataQuery,
  useUploadSITSalesDataMutation,
  useDeleteSITSalesDataMutation,
  useLazyGetSITSalesDataTemplateQuery,
  useGetSITPayrollDataQuery,
  useUploadSITPayrollDataMutation,
  useDeleteSITPayrollDataMutation,
  useLazyGetSITPayrollTemplateQuery,
  useGetSITPropertyDataQuery,
  useAddSITPropertyDataMutation,
  useDeleteSITPropertyDataMutation,
  useGetSITSettingsQuery,
  useSaveSITSettingsMutation,
  usePatchSITTaxableIncomeMutation,
  useExportSITOverviewMutation,
} = stateIncomeTaxApi;
